// lib/browser-error-buffer.ts
// Holds window errors and unhandled rejections that fire before the Sentry
// browser SDK has been loaded, so instrumentation-client.ts can defer the
// ~400 KB SDK chunk until the page is idle without losing early errors.
// Takes anything with add/removeEventListener (window in production).

export type ErrorEventTarget = Pick<Window, "addEventListener" | "removeEventListener">;

export interface ErrorBuffer {
  /** Stop listening and hand every buffered error to `report`, oldest first. */
  flush(report: (error: unknown) => void): void;
  /** Number of errors currently held. */
  size(): number;
}

const MAX_BUFFERED = 20;

export function bufferEarlyErrors(
  target: ErrorEventTarget,
  max: number = MAX_BUFFERED,
): ErrorBuffer {
  const held: unknown[] = [];
  const push = (error: unknown) => {
    if (held.length < max) held.push(error);
  };
  const onError = (event: ErrorEvent) =>
    push(event.error ?? event.message ?? "Unknown error");
  const onRejection = (event: PromiseRejectionEvent) => push(event.reason);

  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);

  return {
    flush(report) {
      target.removeEventListener("error", onError);
      target.removeEventListener("unhandledrejection", onRejection);
      for (const error of held.splice(0)) report(error);
    },
    size: () => held.length,
  };
}

/** Runs `task` once the page has loaded and the main thread is idle. */
export function runWhenIdleAfterLoad(task: () => void): void {
  const idle = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(task, { timeout: 4000 });
    } else {
      setTimeout(task, 1);
    }
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}
